import {action, makeObservable, observable, reaction, runInAction} from "mobx";
import {IUserService} from "../../../services/userService";
import {INoticeService} from "../../../services/noticeService";
import {NoticeStatus} from "../../../shared/enums/noticeStatus";
import {SortOrder} from "../../../shared/enums/sortOrder";
import NoticeItem from "./models/noticeItem";
import NoticesDataListItem from "./models/noticesDataListItem";
import {AuthUser} from "../../../utils/auth/user";
import {KeyValue} from "../../observables/keyValue";
import {
  allVillageKeyValue,
  allVillagesInAU,
  allVillagesInNZ
} from "../../../shared/constants";
import {SaveNoticeDetails} from "../../../shared/interfaces/saveNoticeDetails";
import {FileCreated, IFileService} from "../../../services/fileService";
import {NoticeType} from "../../../shared/enums/noticeType";

/**
 * The interface for NoticeDomainStore.
 */
export interface INoticesDomainStore {
  /**
   * An array of notices.
   */
  noticesList: NoticesDataListItem[];
  /**
   * An editable notice that contains the notice detail.
   */
  editableNotice: NoticeItem;
  reloadEditableNotice: () => Promise<void>;
  /**
   * Loads the notice details from the backend and set the editableNotice property of NoticesDomainStore.
   */
  loadEditableNotice: (id: string) => Promise<void>;
  resetEditableNotice: () => void;
  updateNotice: (isDraft: boolean) => Promise<SaveNoticeDetails>;
  createNotice: () => Promise<SaveNoticeDetails>;
  unpublishNotice: (id: string) => Promise<string>;

  /**
   * Deletes the selected notice.
   */
  deleteNotice: () => Promise<string>;

  /**
   * Loads the paginated notices from the backend and set the noticesList property of NoticesDomainStore.
   * The method returns the total count of notices currently in the data store.
   */
  loadNoticeArticles: (
    villageId: string,
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => Promise<number>;
  invalidateNoticeArticleRequests: () => void;

  /**
   * A selected notice to be acted on.
   */
  selectedNotice: NoticesDataListItem;

  /**
   * Village ID that is selected.
   */
  selectedVillageId?: string;

  /**
   * Gets list of village IDs.
   */
  getVillages: () => void;

  /**
   * Get the time zone of the selected village.
   */
  getTimeZone: () => string;

  /**
   * Load image of the editable notice.
   */
  getNoticeImage: () => Promise<void>;
}

/**
 * The store that manage the logic and state of notice domain objects.
 */
export class NoticesDomainStore implements INoticesDomainStore {
  private readonly noticeService: INoticeService;
  private readonly userService: IUserService;
  private readonly fileService: IFileService;
  multiSelectVillages: KeyValue[] = [];
  noticesList: NoticesDataListItem[] = [];
  editableNotice: NoticeItem = new NoticeItem(
    "",
    "",
    "",
    "",
    NoticeStatus.Published,
    null
  );

  selectedNotice: NoticesDataListItem = new NoticesDataListItem(
    "",
    "",
    NoticeStatus.Published,
    null,
    "",
    null,
    null,
    null
  );
  selectedVillageId?: string;
  private noticeArticlesRequestId = 0;

  /**
   * Instantiates a new NoticesDomainStore.
   */
  constructor(
    noticeService: INoticeService,
    userService: IUserService,
    fileService: IFileService
  ) {
    makeObservable(this, {
      noticesList: observable,
      editableNotice: observable,
      resetEditableNotice: action,
      updateNotice: action,
      createNotice: action,
      unpublishNotice: action
    });
    this.noticeService = noticeService;
    this.userService = userService;
    this.fileService = fileService;

    reaction(
      () => this.getTimeZone(),
      () => {
        this.editableNotice = new NoticeItem(
          "",
          "",
          "",
          "",
          NoticeStatus.Published,
          null,
          null,
          null,
          null,
          false,
          "",
          undefined,
          undefined,
          this.getTimeZone()
        );
      }
    );
  }

  loadNoticeArticles = async (
    villageId: string,
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => {
    const requestId = ++this.noticeArticlesRequestId;
    const data = await this.noticeService.getNotices(
      villageId,
      pageNumber,
      sizePerPage,
      sortField,
      sortOrder
    );

    runInAction(() => {
      if (requestId === this.noticeArticlesRequestId) {
        this.noticesList = data.notices.map(
          (d: any) =>
            new NoticesDataListItem(
              d.id,
              d.title,
              this.toNoticeStatus(d.status),
              d.publishDate ? new Date(d.publishDate) : null,
              d.author,
              d.readCount,
              d.villageIds,
              d.notificationDate,
              d.isMultiVillage,
              d.isAnnouncement,
              d.isImportant,
              d.personTypes
            )
        );
      }
    });
    return data.totalCount;
  };

  invalidateNoticeArticleRequests = () => {
    this.noticeArticlesRequestId += 1;
  };

  //used for loading a notice from the current id of the selected notice
  reloadEditableNotice = async () => {
    const noticeIdToLoad = this.selectedNotice.id;
    this.resetEditableNotice();
    await this.loadEditableNotice(noticeIdToLoad);
  };

  private getNoticeType = (data: any): NoticeType => {
    if (data.isAnnouncement) {
      return NoticeType.Announcement;
    }
    if (data.isImportant) {
      return NoticeType.Important;
    }
    return NoticeType.General;
  };

  loadEditableNotice = async (id: string) => {
    const data = await this.noticeService.getNotice(id);
    runInAction(() => {
      this.editableNotice = new NoticeItem(
        data.id,
        data.title,
        data.body ?? "",
        data.author ?? "",
        this.toNoticeStatus(data.status),
        data.publishDate ? new Date(data.publishDate) : null,
        data.expiryDate === null ? null : new Date(data.expiryDate),
        data.readCount,
        data.notificationDate ? new Date(data.notificationDate) : null,
        !!data.notificationDate,
        data.creation?.creatorName,
        data.villageIds,
        data.isMultiVillage,
        this.getTimeZone(),
        data.fileId,
        data.imageDescription,
        data.ignoreQuietHours,
        this.getNoticeType(data),
        data.personTypes
      );
      this.getVillages();
    });
    await this.getNoticeImage();
  };

  getVillages = () => {
    this.multiSelectVillages =
      AuthUser.getInstance().Villages?.map(
        (village) => new KeyValue(village.id, village.name)
      ) ?? [];

    if (this.editableNotice.isEditMode) {
      // Set selected villages when editing a notice.
      this.setNoticeSelectedVillages();
    }

    this.multiSelectVillages.unshift(allVillagesInNZ);
    this.multiSelectVillages.unshift(allVillagesInAU);
    this.multiSelectVillages.unshift(allVillageKeyValue);
    this.editableNotice.villages.setItems(this.multiSelectVillages);
  };

  setNoticeSelectedVillages = () => {
    // VillageIds null refers to 'All Village'
    if (this.editableNotice.villageIds === null) {
      this.editableNotice.villages.setSelectedItems(this.multiSelectVillages);
      this.editableNotice.villages.setIsDirty(false);
      return;
    }

    const selectedVillageIds = this.editableNotice.villageIds!;
    const selectedVillageKeyValues = this.multiSelectVillages.filter((value) =>
      selectedVillageIds.find((villageId) => villageId === value.value)
    );
    this.editableNotice.villages.setSelectedItems(selectedVillageKeyValues);
    this.editableNotice.villages.setIsDirty(false);
  };

  resetEditableNotice = () => {
    runInAction(() => {
      // Success/Error toasts rely on this state being preserved
      const previousStatus = this.editableNotice.status;
      this.editableNotice = new NoticeItem(
        "",
        "",
        "",
        "",
        previousStatus,
        null,
        null,
        null,
        null,
        false,
        "",
        undefined,
        undefined,
        this.getTimeZone()
      );
      this.getVillages();
    });
  };

  updateNotice = async (
    isDraftOrScheduled: boolean = false
  ): Promise<SaveNoticeDetails> => {
    if (
      !this.editableNotice.notificationDate &&
      this.editableNotice.notificationEnabled.value
    ) {
      this.editableNotice.notificationDate =
        this.editableNotice.selectedPublishDate.date ??
        this.editableNotice.noticeNow;
    }
    if (isDraftOrScheduled) {
      this.editableNotice.notificationDate = this.editableNotice
        .notificationEnabled.value
        ? this.editableNotice.selectedPublishDate.date ??
          this.editableNotice.noticeNow
        : null;
    }
    this.editableNotice.selectedVillageId =
      this.userService.getSelectedVillage()?.id;

    await this.checkForNewImageAndUpload();

    return await this.noticeService
      .updateNotice(this.editableNotice)
      .then(() => {
        const title = this.editableNotice.title.text;
        const selectedPublishDate =
          this.editableNotice.selectedPublishDate.date;
        const selectedPublishType = this.editableNotice.selectedPublishType;
        const status = this.editableNotice.status;
        this.resetEditableNotice();
        return {title, selectedPublishDate, selectedPublishType, status};
      });
  };

  createNotice = async (): Promise<SaveNoticeDetails> => {
    if (this.editableNotice.status === NoticeStatus.Draft) {
      this.editableNotice.publishDate = null;
    }
    this.editableNotice.notificationDate = this.editableNotice
      .notificationEnabled.value
      ? this.editableNotice.selectedPublishDate.date ??
        this.editableNotice.noticeNow
      : null;
    this.editableNotice.selectedVillageId =
      this.userService.getSelectedVillage()?.id;

    await this.checkForNewImageAndUpload();

    return await this.noticeService.saveNotice(this.editableNotice).then(() => {
      const title = this.editableNotice.title.text;
      const selectedPublishDate = this.editableNotice.selectedPublishDate.date;
      const selectedPublishType = this.editableNotice.selectedPublishType;
      const status = this.editableNotice.status;
      this.resetEditableNotice();
      return {title, selectedPublishDate, selectedPublishType, status};
    });
  };

  unpublishNotice = async (id: string) => {
    const data = await this.noticeService.getNotice(id);
    let toUnpublish = new NoticeItem(
      data.id,
      data.title,
      data.body,
      data.author,
      NoticeStatus.Expired,
      new Date(data.publishDate),
      new Date(),
      null,
      data.notificationDate ? new Date(data.notificationDate) : null,
      false,
      data.creatorName,
      data.villageIds,
      data.isMultiVillage,
      this.getTimeZone(),
      data.fileId,
      data.imageDescription,
      data.ignoreQuietHours,
      this.getNoticeType(data)
    );
    toUnpublish.selectedVillageId = this.userService.getSelectedVillage()?.id;
    toUnpublish.setMultiVillageNotice();
    return await this.noticeService.updateNotice(toUnpublish).then(() => {
      this.resetEditableNotice();
      return toUnpublish.title.text;
    });
  };

  private toNoticeStatus = (status: string): NoticeStatus => {
    switch (status.toUpperCase()) {
      case "PUBLISHED":
        return NoticeStatus.Published;

      case "UNPUBLISHED":
        return NoticeStatus.Unpublished;

      case "SCHEDULED":
        return NoticeStatus.Scheduled;

      case "EXPIRED":
        return NoticeStatus.Expired;

      case "DRAFT":
      default:
        return NoticeStatus.Draft;
    }
  };

  /**
   * Delete a notice with the selected notice ID.
   */
  deleteNotice = async () => {
    return this.noticeService.deleteNotice(this.selectedNotice.id);
  };

  getTimeZone = (): string => {
    return this.userService.getSelectedVillage()?.timeZone!;
  };

  getNoticeImage = async () => {
    if (this.editableNotice.image.fileId !== null) {
      let imageData = await this.fileService.getFile(
        this.editableNotice.image.fileId
      );
      this.editableNotice.image.setImageFromDownload(imageData);
    }
  };

  private async checkForNewImageAndUpload(): Promise<void> {
    if (
      this.editableNotice.image.hasNewUpload &&
      this.editableNotice.image.file !== null
    ) {
      return this.fileService
        .uploadFile(
          this.editableNotice.image.file,
          this.editableNotice.image.fileName
        )
        .then((response: FileCreated) => {
          this.editableNotice.image.fileId = response?.fileId;
        });
    }
  }
}
